import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import api from '../utils/api';

const UserChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const messagesEnd = useRef(null);

  useEffect(() => {
    checkUnread();
    const interval = setInterval(checkUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [open]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const checkUnread = async () => {
    try {
      const res = await api.get('/chat/unread-count');
      setUnread(res.data.unread || 0);
    } catch {}
  };

  const fetchMessages = async () => {
    try {
      const res = await api.get('/chat/messages');
      setMessages(res.data || []);
      setUnread(0);
    } catch {}
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    setLoading(true);
    try {
      await api.post('/chat/send', { message: input });
      setInput('');
      await fetchMessages();
    } catch {}
    setLoading(false);
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full shadow-2xl shadow-cyan-500/30 flex items-center justify-center transition-all hover:scale-110"
          data-testid="user-chat-button"
        >
          <MessageCircle className="w-6 h-6" />
          {unread > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[11px] rounded-full min-w-[22px] h-[22px] flex items-center justify-center font-bold animate-bounce shadow-lg shadow-red-500/50">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-6 left-6 z-50 w-[340px] sm:w-[380px] h-[480px] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden" data-testid="user-chat-window">
          {/* Header */}
          <div className="bg-cyan-500/10 border-b border-cyan-500/20 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2" dir="rtl">
              <div className="w-8 h-8 bg-cyan-500/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">الدعم الفني</p>
                <p className="text-cyan-400 text-[10px]">نرد عليك بأسرع وقت</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white p-1"
              data-testid="user-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2" dir="rtl">
            {messages.length === 0 && (
              <div className="text-center text-zinc-500 py-8 text-sm">
                مرحباً! كيف نقدر نساعدك؟
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.sender === 'user' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] p-2.5 rounded-xl text-sm ${
                  m.sender === 'user'
                    ? 'bg-zinc-800 border border-zinc-700 text-white'
                    : 'bg-cyan-500/10 border border-cyan-500/20 text-cyan-100'
                }`}>
                  <p>{m.message}</p>
                  <p className="text-[9px] opacity-40 mt-1">
                    {m.sender === 'admin' ? 'الدعم' : 'أنت'} - {new Date(m.created_at).toLocaleTimeString('ar-SA', {hour: '2-digit', minute: '2-digit'})}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEnd} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-zinc-800" dir="rtl">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && sendMessage()}
                placeholder="اكتب رسالة..."
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500"
                data-testid="user-chat-input"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white p-2 rounded-xl transition-colors"
                data-testid="user-chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserChat;
